/**
 * Charts.
 *
 * Every chart here follows the same three rules:
 *   1. The bars are decoration; the numbers are the content. Each chart ships
 *      with a written summary and, where it helps, a real table.
 *   2. Nothing is distinguished by colour alone -- a category's share is
 *      labelled in words and percent next to its bar.
 *   3. The whole thing is one `role="img"` with a full text alternative, so a
 *      screen reader hears the finding rather than forty numbers.
 */
import { formatMoney, formatMonthLabel, percentOf } from '@wis/shared';
import type { CategoryTotal, MonthlyTotal } from '@wis/shared';

export interface MonthlyTrendChartProps {
  data: MonthlyTotal[];
  /** The month to emphasise, usually the current one. */
  highlightMonth?: string;
  timeZone?: string;
}

export function MonthlyTrendChart({
  data,
  highlightMonth,
  timeZone,
}: MonthlyTrendChartProps): JSX.Element {
  const max = Math.max(1, ...data.map((entry) => Math.abs(entry.totalSen)));
  const summary = data
    .map((entry) => `${formatMonthLabel(entry.monthKey, timeZone)}: ${formatMoney(entry.totalSen)}`)
    .join('; ');

  return (
    <div className="chart">
      <div className="bar-chart" role="img" aria-label={`Monthly spending. ${summary}.`}>
        {data.map((entry) => {
          const height = Math.max(2, Math.round((Math.abs(entry.totalSen) / max) * 100));
          const isHighlight = entry.monthKey === highlightMonth;
          return (
            <div className="bar-chart__column" key={entry.monthKey}>
              <div
                className="bar-chart__bar"
                style={{ height: `${height}%` }}
                data-emphasis={isHighlight ? 'strong' : 'muted'}
              />
              <span className="bar-chart__label">
                {formatMonthLabel(entry.monthKey, timeZone).slice(0, 3)}
              </span>
            </div>
          );
        })}
      </div>
      <p className="chart-summary">{describeTrend(data, timeZone)}</p>
    </div>
  );
}

function describeTrend(data: MonthlyTotal[], timeZone?: string): string {
  if (data.length < 2) return 'Not enough history yet to show a trend.';
  const latest = data[data.length - 1];
  const previous = data[data.length - 2];
  if (!latest || !previous) return '';

  const label = formatMonthLabel(latest.monthKey, timeZone);
  if (previous.totalSen === 0) {
    return `${label}: ${formatMoney(latest.totalSen)}. Nothing recorded the month before.`;
  }
  const difference = latest.totalSen - previous.totalSen;
  const direction = difference > 0 ? 'more' : difference < 0 ? 'less' : 'the same as';
  const percent = Math.abs(percentOf(difference, previous.totalSen));
  if (difference === 0) return `${label}: ${formatMoney(latest.totalSen)}, ${direction} last month.`;
  return `${label}: ${formatMoney(latest.totalSen)} - ${formatMoney(Math.abs(difference))} (${percent}%) ${direction} than ${formatMonthLabel(previous.monthKey, timeZone)}.`;
}

export interface CategoryBreakdownProps {
  data: CategoryTotal[];
  toneFor: (categoryId: string) => string;
  /** Shows every category rather than the top few. */
  showAll?: boolean;
}

export function CategoryBreakdown({
  data,
  toneFor,
  showAll = false,
}: CategoryBreakdownProps): JSX.Element {
  const rows = showAll ? data : data.slice(0, 6);
  const hidden = data.length - rows.length;

  if (data.length === 0) {
    return <p className="small muted">No spending in this period yet.</p>;
  }

  const summary = rows
    .map((row) => `${row.name} ${formatMoney(row.totalSen)}, ${row.share}%`)
    .join('; ');

  return (
    <div className="chart">
      <div className="breakdown" role="img" aria-label={`Spending by category. ${summary}.`}>
        {rows.map((row) => (
          <div className="breakdown__row" key={row.categoryId}>
            <span className="breakdown__name">{row.name}</span>
            <span className="small">
              <Money sen={row.totalSen} /> <span className="faint">{row.share}%</span>
            </span>
            <div className="breakdown__track">
              <div
                className="breakdown__fill"
                style={{
                  width: `${Math.max(2, row.share)}%`,
                  // The tone is a token, so light and dark stay coherent.
                  ['--tone' as string]: `var(--tone-${toneFor(row.categoryId)})`,
                }}
              />
            </div>
          </div>
        ))}
      </div>
      {hidden > 0 ? (
        <p className="chart-summary">
          {hidden} smaller {hidden === 1 ? 'category' : 'categories'} not shown.
        </p>
      ) : null}
    </div>
  );
}

function Money({ sen }: { sen: number }): JSX.Element {
  return <span className="money">{formatMoney(sen)}</span>;
}

export interface DailySparkProps {
  data: { dayKey: string; totalSen: number }[];
  average: number;
}

/** A month at a glance: one column per day, with the average drawn across it. */
export function DailySpark({ data, average }: DailySparkProps): JSX.Element {
  const max = Math.max(1, ...data.map((entry) => Math.abs(entry.totalSen)));
  const busiest = data.reduce(
    (best, entry) => (entry.totalSen > (best?.totalSen ?? 0) ? entry : best),
    data[0],
  );

  return (
    <div className="chart">
      <div
        className="bar-chart"
        style={{ height: 90 }}
        role="img"
        aria-label={`Daily spending this month. Highest day ${busiest?.dayKey ?? 'none'} at ${formatMoney(busiest?.totalSen ?? 0)}. Daily average ${formatMoney(average)}.`}
      >
        {data.map((entry) => (
          <div className="bar-chart__column" key={entry.dayKey}>
            <div
              className="bar-chart__bar"
              style={{ height: `${Math.max(2, Math.round((Math.abs(entry.totalSen) / max) * 100))}%` }}
              data-emphasis={entry.totalSen > average ? 'strong' : 'muted'}
            />
          </div>
        ))}
      </div>
      <p className="chart-summary">
        Daily average {formatMoney(average)}. Bars above the average are drawn darker; the highest day
        was {busiest ? busiest.dayKey.slice(8) : '-'} at {formatMoney(busiest?.totalSen ?? 0)}.
      </p>
    </div>
  );
}

/** The same numbers as a table, for anyone who would rather read them. */
export function CategoryTable({ data }: { data: CategoryTotal[] }): JSX.Element {
  return (
    <div className="scroll-x">
      <table className="data-table">
        <caption className="visually-hidden">Spending by category</caption>
        <thead>
          <tr>
            <th scope="col">Category</th>
            <th scope="col">Payments</th>
            <th scope="col">Total</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.categoryId}>
              <th scope="row">{row.name}</th>
              <td>{row.count}</td>
              <td className="money">{formatMoney(row.totalSen)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
